import { useCallback } from "react";
import indexOf from "lodash/indexOf";

import OptionInput from "./OptionInput";
import OtherField from "./OtherField";
import inputConnect from "./inputConnect";
import classNames from "classnames";

const CheckBoxesInput = ({ displayOtherField = false, ...props }) => {
  const {
    values,
    updateValue,
    isCorrection,
    correctionTextOnlyOnWrongAnswers,
    fieldName,
    otherFieldLabel,
    other,
    otherValue,
    filterUnusedOptions,
    maximumSelectableOptions
  } = props;

  const identifierIndex = useCallback(
    identifier => indexOf(values, identifier),
    [values]
  );

  const isChecked = useCallback(
    identifier => identifierIndex(identifier) >= 0,
    [identifierIndex]
  );

  const currentOptions = props.options;
  const filteredOptionValues = currentOptions.filter(
    option => isChecked(option.id) || option.isCorrect
  );

  const options =
    isCorrection && filterUnusedOptions ? filteredOptionValues : currentOptions;

  const update = useCallback(
    data => {
      const currentData = { values, other, otherValue };

      updateValue({ ...currentData, ...data });
    },
    [other, otherValue, updateValue, values]
  );

  const handleOptionChanged = useCallback(
    e => {
      const { value, checked } = e.target;
      const wasChecked = isChecked(value);
      const newValues = [...values];

      if (checked && !wasChecked) {
        newValues.push(value);
      } else if (!checked && wasChecked) {
        const targetValueIdentifierIndex = identifierIndex(value);

        newValues.splice(targetValueIdentifierIndex, 1);
      }

      update({ values: newValues });
    },
    [identifierIndex, isChecked, update, values]
  );

  const handleOtherChanged = useCallback(value => update(value), [update]);

  const className = classNames("check-boxes", props.className);

  // Determine if we've reached the maximum number of selections
  // including the other field
  const currentSelectionCount = (values?.length || 0) + (other ? 1 : 0);
  const hasReachedMaximum =
    maximumSelectableOptions && currentSelectionCount >= maximumSelectableOptions;

  return (
    <div className={className}>
      {options.map(option => {
        const checked = isChecked(option.id);
        const disabled = hasReachedMaximum && !checked;

        return (
          <OptionInput
            key={option.id}
            type="checkbox"
            option={option}
            value={option.id}
            isCorrection={isCorrection}
            correctionTextOnlyOnWrongAnswers={correctionTextOnlyOnWrongAnswers}
            name={fieldName}
            onChange={handleOptionChanged}
            checked={checked}
            disabled={disabled}
          />
        );
      })}

      <OtherField
        inputType="checkbox"
        fieldName={fieldName}
        displayOtherField={displayOtherField}
        otherFieldLabel={otherFieldLabel}
        other={other}
        otherValue={otherValue}
        onChange={handleOtherChanged}
        disabled={hasReachedMaximum && !other}
      />
    </div>
  );
};

export default inputConnect(CheckBoxesInput, {
  fields: { values: [], other: false, otherValue: "" }
});
